/**
 * useCollectionImport — all state + logic for the collection import modal.
 *
 * Extracted from ImportModal.tsx so the modal becomes a thin render shell.
 * Owns: step, raw input, detected format, matched cards, duplicate stats,
 * import-mode, folder destination, printing preference, import progress, result.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAtomValue } from 'jotai';

import { useAddCardsMutation } from '@/features/collection/hooks/api';
import { collectionStateAtom } from '@/features/collection/state/collectionAtom';
import { parseDeckList } from '@/features/decks/lib/deckImportExport';
import { useFoldersQuery } from '@/features/folders/hooks/api';
import useCardIndex from '@/hooks/useCardIndex';
import useImportSteps from '@/hooks/useImportSteps';
import { SOURCE_LABELS } from '@/lib/cardUtils';
import type { ImportFieldSummary, MatchingStrategy } from '@/lib/universalCSVParser';
import {
  deleteCardsByFolder,
  deleteCardsBySource,
  getCountByFolder,
  getCountBySource,
  updateCollectionCardsQuantity,
} from '@/services/collection';
import { bumpFolderUpdatedAt, getOrCreateFolderUnderParent } from '@/services/folders';
import type { ImportSource } from '@/types/card';
import type { CreateCollectionCard } from '@/types/schemas';

import type { FolderDestination } from '../components/FolderDestinationSelector';
import type { ImportMode, OverwriteContext } from '../components/ImportModeSelector';
import type { PrintingPreference } from '../lib/formatAdapters';
import {
  collectionBackupToImportCards,
  deckBackupToImportCards,
  deckCardsToImportCards,
  listBackupToImportCards,
  remapImportCardsToCollection,
} from '../lib/formatAdapters';
import { detectMtgCraftBackupType, isMtgCraftBackup, parseCollectionBackup, parseDeckBackup, parseListBackup } from '../parsers/mtgcraft';
import { detectFormat, parseImport } from '../parsers/parseImport';
import type { ImportTabId } from '../types';
import { useDuplicateDetection, type MatchedImportCardWithDuplicate } from './useDuplicateDetection';

// ============================================================================
// Constants
// ============================================================================

const IMPORT_STEPS = ['input', 'preview', 'importing', 'complete'] as const;
export type CollectionImportStep = (typeof IMPORT_STEPS)[number];

// ============================================================================
// Hook
// ============================================================================

export default function useCollectionImport(onClose: () => void) {
  // Step management
  const steps = useImportSteps(IMPORT_STEPS);

  // Input state
  const [activeTab, setActiveTab] = useState<ImportTabId>('upload');
  const [pasteText, setPasteText] = useState('');
  const [selectedSource, setSelectedSource] = useState<ImportSource | null>(null);
  const [detectedSource, setDetectedSource] = useState<ImportSource | null>(null);

  // Parse / match state
  const [matchedCards, setMatchedCards] = useState<MatchedImportCardWithDuplicate[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [isMatching, setIsMatching] = useState(false);
  const [fieldSummary, setFieldSummary] = useState<ImportFieldSummary | null>(null);
  const [detectedStrategy, setDetectedStrategy] = useState<MatchingStrategy>('none');

  // Import-option state
  const [importMode, setImportMode] = useState<ImportMode>('add_all');
  const [folderDestination, setFolderDestination] = useState<FolderDestination>({ type: 'root' });
  const [useImportFolders, setUseImportFolders] = useState(true);
  const [duplicateStats, setDuplicateStats] = useState({ new: 0, duplicate: 0 });
  const [printingPreference, setPrintingPreference] = useState<PrintingPreference>('exact');

  // Source-system state
  const [previewSource, setPreviewSource] = useState<ImportSource | null>(null);
  const [rawImportText, setRawImportText] = useState('');
  const [isCrossImport, setIsCrossImport] = useState(false);
  const [crossImportInfo, setCrossImportInfo] = useState<string | null>(null);

  // Progress / result state
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResult, setImportResult] = useState({ success: 0, failed: 0, updated: 0 });
  const [existingSourceCount, setExistingSourceCount] = useState(0);
  const [existingFolderCount, setExistingFolderCount] = useState(0);

  // External hooks
  const addCardsMutation = useAddCardsMutation();
  const { matchImportCards, getCardById, getCardByName, getCardByOracleId } = useCardIndex();
  const collectionState = useAtomValue(collectionStateAtom);
  const { data: folders = [] } = useFoldersQuery();
  const { detectDuplicates } = useDuplicateDetection();

  // ---- Derived values ---------------------------------------------------

  const matchingStrategy: MatchingStrategy = fieldSummary?.matchingStrategy ?? detectedStrategy;
  const effectiveSource: ImportSource = previewSource ?? detectedSource ?? selectedSource ?? 'unknown';
  const hasImportFolders = useMemo(() => matchedCards.some((c) => c.folderName), [matchedCards]);

  // Overwrite context
  const overwriteContext = useMemo((): OverwriteContext => {
    if (effectiveSource !== 'unknown' && existingSourceCount > 0) {
      return { type: 'source', sourceName: SOURCE_LABELS[effectiveSource] ?? effectiveSource, count: existingSourceCount };
    }
    if (folderDestination.type === 'folder' && existingFolderCount > 0) {
      const folder = folders.find((f) => f.id === folderDestination.folderId);
      return { type: 'folder', folderName: folder?.name ?? 'Selected folder', count: existingFolderCount };
    }
    return { type: 'disabled' };
  }, [effectiveSource, existingSourceCount, folderDestination, existingFolderCount, folders]);

  // Importable count
  const importableCount = useMemo(() => {
    const validCards = matchedCards.filter((c) => c.matched && (c.matchType === 'exact' || c.matchType === 'fuzzy'));
    switch (importMode) {
      case 'skip':
        return validCards.filter((c) => c.duplicateStatus === 'new').length;
      case 'update':
      case 'overwrite':
      case 'add_all':
      default:
        return validCards.length;
    }
  }, [matchedCards, importMode]);

  // Import button text
  const importButtonText = useMemo(() => {
    if (importMode === 'update') {
      const newCount = matchedCards.filter((c) => c.duplicateStatus === 'new' && c.matched).length;
      const updateCount = matchedCards.filter((c) => c.duplicateStatus === 'duplicate' && c.matched).length;
      if (updateCount > 0 && newCount > 0) return `Import ${newCount} New, Update ${updateCount}`;
      if (updateCount > 0) return `Update ${updateCount} Cards`;
    }
    return `Import ${importableCount} Cards`;
  }, [importMode, importableCount, matchedCards]);

  // ---- Side-effects -----------------------------------------------------

  // Check existing cards for source count
  useEffect(() => {
    if (steps.currentStep === 'preview' && effectiveSource !== 'unknown') {
      getCountBySource(effectiveSource)
        .then(setExistingSourceCount)
        .catch(() => setExistingSourceCount(0));
    } else {
      setExistingSourceCount(0);
    }
  }, [effectiveSource, steps.currentStep]);

  // Check existing cards for folder count
  useEffect(() => {
    if (steps.currentStep === 'preview' && folderDestination.type === 'folder') {
      getCountByFolder(folderDestination.folderId)
        .then(setExistingFolderCount)
        .catch(() => setExistingFolderCount(0));
    } else {
      setExistingFolderCount(0);
    }
  }, [folderDestination, steps.currentStep]);

  // Auto-switch away from overwrite when unavailable
  useEffect(() => {
    if (importMode === 'overwrite' && overwriteContext.type === 'disabled') {
      setImportMode('add_all');
    }
  }, [importMode, overwriteContext]);

  // ---- Actions ----------------------------------------------------------

  const resetState = useCallback(() => {
    steps.reset();
    setActiveTab('upload');
    setPasteText('');
    setSelectedSource(null);
    setDetectedSource(null);
    setMatchedCards([]);
    setParseErrors([]);
    setIsMatching(false);
    setImportProgress({ current: 0, total: 0 });
    setImportResult({ success: 0, failed: 0, updated: 0 });
    setExistingSourceCount(0);
    setExistingFolderCount(0);
    setFieldSummary(null);
    setDetectedStrategy('none');
    setImportMode('add_all');
    setFolderDestination({ type: 'root' });
    setUseImportFolders(true);
    setDuplicateStats({ new: 0, duplicate: 0 });
    setPreviewSource(null);
    setCrossImportInfo(null);
    setPrintingPreference('exact');
    setRawImportText('');
    setIsCrossImport(false);
  }, [steps]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const processImportText = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      let importCards: import('@/types/schemas').ImportCard[] | null = null;
      let crossInfo: string | null = null;
      const cardIndex = { getCardById, getCardByName, getCardByOracleId };

      setRawImportText(trimmed);

      // Check for MtgCraft deck/list backup cross-imports
      if (isMtgCraftBackup(trimmed)) {
        const backupType = detectMtgCraftBackupType(trimmed);

        if (backupType === 'single_deck' || backupType === 'multi_deck') {
          const backupResult = parseDeckBackup(trimmed);
          if (backupResult.decks.length > 0) {
            const allCards = backupResult.decks.flatMap((d) => d.cards);
            importCards = deckBackupToImportCards(allCards, cardIndex);
            crossInfo = 'Detected MtgCraft deck backup — importing as collection cards (zone info will be discarded).';
          }
          if (backupResult.errors.length > 0) setParseErrors(backupResult.errors);
        } else if (backupType === 'single_list' || backupType === 'multi_list') {
          const backupResult = parseListBackup(trimmed);
          if (backupResult.lists.length > 0) {
            const allCards = backupResult.lists.flatMap((l) => l.cards);
            importCards = listBackupToImportCards(allCards, cardIndex);
            crossInfo = 'Detected MtgCraft list backup — importing as collection cards (priority and notes will be discarded).';
          }
          if (backupResult.errors.length > 0) setParseErrors(backupResult.errors);
        } else if (backupType === 'collection') {
          const backupResult = parseCollectionBackup(trimmed);
          if (backupResult.cards.length > 0) {
            importCards = collectionBackupToImportCards(backupResult.cards, backupResult.folders, cardIndex);
          }
          if (backupResult.errors.length > 0) setParseErrors(backupResult.errors);
        }
      }

      // Try deck text format if no cross-import and not a backup
      if (!importCards && !isMtgCraftBackup(trimmed)) {
        const deckResult = parseDeckList(trimmed);
        if (deckResult.cards.length > 0 && deckResult.hasZoneDefinitions) {
          importCards = deckCardsToImportCards(deckResult.cards, cardIndex);
          crossInfo = 'Detected deck list format — importing as collection cards (zone info will be discarded).';
        }
      }

      // Collection backup: native restore path
      if (importCards && importCards.length > 0 && !crossInfo) {
        setCrossImportInfo(null);
        setDetectedSource(null);
        setFieldSummary(null);
        setDetectedStrategy('fingerprint');
        setIsCrossImport(false);

        setIsMatching(true);
        try {
          await new Promise((resolve) => setTimeout(resolve, 10));
          const matched = matchImportCards(importCards);
          const duplicateResult = detectDuplicates(matched, 'fingerprint');
          setMatchedCards(duplicateResult.cards);
          setDuplicateStats({ new: duplicateResult.newCount, duplicate: duplicateResult.duplicateCount });
          setImportMode('add_all');
          setPreviewSource(null);
          steps.goTo('preview');
        } catch (error) {
          setParseErrors([`Processing error: ${error instanceof Error ? error.message : 'Unknown error'}`]);
        } finally {
          setIsMatching(false);
        }
        return;
      }

      // Cross-import path
      if (importCards && importCards.length > 0) {
        setCrossImportInfo(crossInfo);
        setDetectedSource(null);
        setFieldSummary(null);
        setDetectedStrategy('none');
        setIsCrossImport(true);

        setIsMatching(true);
        try {
          await new Promise((resolve) => setTimeout(resolve, 10));
          const matched = matchImportCards(importCards);
          const duplicateResult = detectDuplicates(matched, 'none');
          setMatchedCards(duplicateResult.cards);
          setDuplicateStats({ new: duplicateResult.newCount, duplicate: duplicateResult.duplicateCount });
          setImportMode('add_all');
          setPreviewSource(null);
          steps.goTo('preview');
        } catch (error) {
          setParseErrors([`Processing error: ${error instanceof Error ? error.message : 'Unknown error'}`]);
        } finally {
          setIsMatching(false);
        }
        return;
      }

      // Standard collection import flow
      setCrossImportInfo(null);
      setIsCrossImport(false);

      const detected = detectFormat(text);
      setDetectedSource(detected);

      const source = selectedSource ?? detected;
      if (!source) {
        setParseErrors(['Could not detect import format. Please select a source manually.']);
        return;
      }

      const result = parseImport(text, source);
      const summary = result.fieldSummary ?? null;
      setFieldSummary(summary);

      let strategy: MatchingStrategy = summary?.matchingStrategy ?? 'none';
      if (!summary && result.cards.length > 0) {
        const firstCard = result.cards[0];
        if (firstCard.sourceId) {
          strategy = 'source_id';
        } else if (firstCard.setCode && firstCard.collectorNumber) {
          strategy = 'fingerprint';
        }
      }
      setDetectedStrategy(strategy);
      setParseErrors(result.errors);

      if (result.cards.length === 0) return;

      setIsMatching(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 10));
        const matched = matchImportCards(result.cards);

        const duplicateResult = detectDuplicates(matched, strategy);
        setMatchedCards(duplicateResult.cards);
        setDuplicateStats({ new: duplicateResult.newCount, duplicate: duplicateResult.duplicateCount });

        if (strategy !== 'none' && duplicateResult.duplicateCount > 0) {
          setImportMode('skip');
        } else {
          setImportMode('add_all');
        }

        setPreviewSource(selectedSource ?? detected ?? null);
        steps.goTo('preview');
      } catch (error) {
        setParseErrors([...result.errors, `Processing error: ${error instanceof Error ? error.message : 'Unknown error'}`]);
      } finally {
        setIsMatching(false);
      }
    },
    [selectedSource, matchImportCards, detectDuplicates, getCardById, getCardByName, getCardByOracleId, steps],
  );

  const handleFileDrop = useCallback(
    async (file: File) => {
      const text = await file.text();
      await processImportText(text);
    },
    [processImportText],
  );

  const handlePreviewSourceChange = useCallback(
    async (newSource: ImportSource) => {
      setPreviewSource(newSource);

      if (isCrossImport || !rawImportText) return;

      const csvSources: ImportSource[] = ['dragonshield', 'delverlens', 'delverlens-legacy', 'moxfield'];
      if (!csvSources.includes(newSource)) return;

      const result = parseImport(rawImportText, newSource);
      if (result.cards.length === 0) return;

      setFieldSummary(result.fieldSummary ?? null);

      const strategy: MatchingStrategy = result.fieldSummary?.matchingStrategy ?? 'none';
      setDetectedStrategy(strategy);

      const matched = matchImportCards(result.cards);
      const duplicateResult = detectDuplicates(matched, strategy);
      setMatchedCards(duplicateResult.cards);
      setDuplicateStats({ new: duplicateResult.newCount, duplicate: duplicateResult.duplicateCount });
    },
    [isCrossImport, rawImportText, matchImportCards, detectDuplicates],
  );

  const handlePasteSubmit = useCallback(async () => {
    if (!pasteText.trim()) return;
    await processImportText(pasteText);
  }, [pasteText, processImportText]);

  const handleImport = useCallback(async () => {
    // Apply printing preference remap
    let effectiveCards = matchedCards;
    if (printingPreference === 'collection' && collectionState.data && collectionState.data.length > 0) {
      const cardIndex = { getCardById, getCardByName, getCardByOracleId };
      effectiveCards = remapImportCardsToCollection(matchedCards, collectionState.data, cardIndex) as MatchedImportCardWithDuplicate[];
    }

    let cardsToAdd: MatchedImportCardWithDuplicate[] = [];
    let cardsToUpdate: MatchedImportCardWithDuplicate[] = [];
    const validCards = effectiveCards.filter((c) => c.matched && (c.matchType === 'exact' || c.matchType === 'fuzzy'));

    switch (importMode) {
      case 'skip':
        cardsToAdd = validCards.filter((c) => c.duplicateStatus === 'new');
        break;
      case 'update':
        cardsToAdd = validCards.filter((c) => c.duplicateStatus === 'new');
        cardsToUpdate = validCards.filter((c) => c.duplicateStatus === 'duplicate' && c.matchedCollectionCardId);
        break;
      case 'overwrite':
      case 'add_all':
        cardsToAdd = validCards;
        break;
    }

    const totalOperations = cardsToAdd.length + cardsToUpdate.length;
    if (totalOperations === 0) {
      setParseErrors(['No valid cards to import']);
      return;
    }

    steps.goTo('importing');
    setImportProgress({ current: 0, total: totalOperations });

    // Overwrite mode: delete existing first
    if (importMode === 'overwrite') {
      try {
        if (overwriteContext.type === 'source') {
          await deleteCardsBySource(effectiveSource);
        } else if (overwriteContext.type === 'folder' && folderDestination.type === 'folder') {
          await deleteCardsByFolder(folderDestination.folderId);
        }
      } catch (error) {
        setParseErrors([`Failed to delete existing cards: ${error instanceof Error ? error.message : 'Unknown error'}`]);
        steps.goTo('preview');
        return;
      }
    }

    // Update mode: update quantities
    let updatedCount = 0;
    if (cardsToUpdate.length > 0) {
      const updates = new Map<string, number>();
      for (const card of cardsToUpdate) {
        if (card.matchedCollectionCardId) {
          updates.set(card.matchedCollectionCardId, card.quantity);
        }
      }
      try {
        updatedCount = await updateCollectionCardsQuantity(updates);
        setImportProgress((prev) => ({ ...prev, current: prev.current + cardsToUpdate.length }));
      } catch (error) {
        setParseErrors([`Failed to update existing cards: ${error instanceof Error ? error.message : 'Unknown error'}`]);
        steps.goTo('preview');
        return;
      }
    }

    // Build collection cards for new additions
    const collectionCards: CreateCollectionCard[] = [];
    const folderCache = new Map<string, string>();
    let baseFolderId: string | null = null;
    if (folderDestination.type === 'folder') baseFolderId = folderDestination.folderId;

    for (const card of cardsToAdd) {
      if (!card.matched) continue;

      let folderId: string | null = null;
      if (useImportFolders && card.folderName) {
        const cacheKey = `${baseFolderId ?? 'root'}:${card.folderName}`;
        if (folderCache.has(cacheKey)) {
          folderId = folderCache.get(cacheKey)!;
        } else {
          try {
            folderId = await getOrCreateFolderUnderParent(card.folderName, baseFolderId);
            folderCache.set(cacheKey, folderId);
          } catch {
            folderId = baseFolderId;
          }
        }
      } else if (folderDestination.type === 'folder') {
        folderId = folderDestination.folderId;
      }

      collectionCards.push({
        scryfall_id: card.matched.id,
        quantity: card.quantity,
        finish: card.finish,
        condition: card.condition,
        language: card.language,
        purchase_price: card.purchasePrice ?? null,
        purchase_currency: card.purchaseCurrency ?? null,
        purchase_date: card.purchaseDate ?? null,
        is_altered: card.isAltered,
        is_playtest: card.isPlaytest,
        is_signed: false,
        notes: null,
        folder_id: folderId,
        source_system: effectiveSource,
        source_id: card.sourceId ?? null,
      });
    }

    let addedCount = 0;
    if (collectionCards.length > 0) {
      try {
        const baseProgress = cardsToUpdate.length;
        const addedIds = await addCardsMutation.mutateAsync({
          cards: collectionCards,
          onBatchCommitted: (n) => setImportProgress({ current: baseProgress + n, total: totalOperations }),
        });
        addedCount = addedIds.length;
      } catch (error) {
        setParseErrors([`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`]);
        steps.goTo('preview');
        return;
      }
    }

    // Bump updated_at on existing folders that received imported cards
    if (addedCount > 0 && folderDestination.type === 'folder' && folderDestination.folderId) {
      await bumpFolderUpdatedAt(folderDestination.folderId).catch(() => {});
    }

    setImportResult({
      success: addedCount,
      updated: updatedCount,
      failed: collectionCards.length - addedCount,
    });
    steps.goTo('complete');
  }, [
    matchedCards,
    effectiveSource,
    addCardsMutation,
    importMode,
    overwriteContext,
    folderDestination,
    useImportFolders,
    printingPreference,
    collectionState.data,
    getCardById,
    getCardByName,
    getCardByOracleId,
    steps,
  ]);

  return {
    // Step
    steps,

    // Input
    activeTab,
    setActiveTab,
    pasteText,
    setPasteText,
    selectedSource,
    setSelectedSource,

    // Parse / match
    matchedCards,
    parseErrors,
    isMatching,
    fieldSummary,
    matchingStrategy,

    // Import options
    importMode,
    setImportMode,
    folderDestination,
    setFolderDestination,
    useImportFolders,
    setUseImportFolders,
    duplicateStats,
    printingPreference,
    setPrintingPreference,
    hasImportFolders,

    // Source
    effectiveSource,
    detectedSource,
    crossImportInfo,

    // Progress / result
    importProgress,
    importResult,
    overwriteContext,
    importableCount,
    importButtonText,
    folders,

    // Actions
    resetState,
    handleClose,
    handleFileDrop,
    handlePasteSubmit,
    handlePreviewSourceChange,
    handleImport,
  } as const;
}
